// src/hooks/useTravelLog.js
// ============================================================================
// Travel log CRUD (public.travel_log). Field locked like useSupabase.js.
//
// RLS decides what comes back: everyone sees their own rows, admins see all.
// Delete is soft (deleted_at), so a removed trip can be recovered in SQL and
// the VOF statement for a closed quarter can be explained after the fact.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { requireSession } from '../lib/supabaseAuthHelpers';
import { formatSupabaseError, normalizeId } from './useSupabase';

const COLUMNS = 'id, user_id, start_date, end_date, country, note, created_by, created_at, updated_at, deleted_at';

const mapRow = (r) => ({
  id: normalizeId(r.id),
  userId: normalizeId(r.user_id),
  startDate: r.start_date,
  endDate: r.end_date,
  country: r.country,
  note: r.note ?? '',
  createdBy: normalizeId(r.created_by),
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  deletedAt: r.deleted_at ?? null,
});

const toDb = (t) => ({
  user_id: normalizeId(t.userId),
  start_date: t.startDate,
  end_date: t.endDate,
  country: String(t.country || '').trim().toUpperCase(),
  note: (t.note || '').trim() || null,
});

export default function useTravelLog({ enabled = true, role } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('travel_log')
      .select(COLUMNS)
      .is('deleted_at', null)
      .order('start_date', { ascending: false });
    if (err) {
      setError(formatSupabaseError(err, { action: 'load', entity: 'travel log', role }));
      setRows([]);
    } else {
      setRows((data || []).map(mapRow));
    }
    setLoading(false);
  }, [role]);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  const create = useCallback(async (travel) => {
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .from('travel_log')
        .insert(toDb(travel))
        .select(COLUMNS)
        .single();
      if (err) return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'travel entry', role }) };
      await refresh();
      return { success: true, data: mapRow(data) };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [refresh, role]);

  const update = useCallback(async (id, travel) => {
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .from('travel_log')
        .update(toDb(travel))
        .eq('id', id)
        .select(COLUMNS);
      if (err) return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'travel entry', role }) };
      // An UPDATE that RLS filters out matches zero rows instead of raising.
      if (!data || data.length === 0) return { success: false, error: 'Not saved: you may not edit this travel entry.' };
      await refresh();
      return { success: true, data: mapRow(data[0]) };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [refresh, role]);

  const remove = useCallback(async (id) => {
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .from('travel_log')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select('id');
      if (err) return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'travel entry', role }) };
      if (!data || data.length === 0) return { success: false, error: 'Not deleted: you may not remove this travel entry.' };
      await refresh();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [refresh, role]);

  return { data: rows, loading, error, create, update, delete: remove, refresh };
}
